import { create } from 'zustand'
import type { Changelog } from '@/types/directus'
import type { User } from '@/lib/types'

// Define the shape of the changelog item used in the store
export type InboxChangelog = Changelog & { isRead: boolean }

const STORAGE_KEY = 'changelogReadStatus'

interface ChangelogState {
  changelogs: InboxChangelog[]
  hasUnread: boolean
  loading: boolean
  error: string | null
  lastFetchedContext: { userId: string | null; locale: string } | null
  lastFetchedTimestamp: number | null
  fetchChangelogs: (locale: string, user: User | null) => Promise<void>
  markAsRead: (changelogId: string) => void
}

export const useChangelogStore = create<ChangelogState>((set, get) => ({
  changelogs: [],
  hasUnread: false,
  loading: true,
  error: null,
  lastFetchedContext: null,
  lastFetchedTimestamp: null,

  fetchChangelogs: async (locale, user) => {
    const ctx = get().lastFetchedContext
    const lastTs = get().lastFetchedTimestamp
    const incomingUserId = user?._id ? String(user._id) : null
    const SIX_HOURS = 6 * 60 * 60 * 1000
    const contextUnchanged =
      !!ctx && ctx.locale === String(locale) && ctx.userId === incomingUserId
    const fresh = typeof lastTs === 'number' && Date.now() - lastTs < SIX_HOURS
    if (contextUnchanged && fresh) return

    set({ loading: true, error: null })
    try {
      // Step 1: Fetch the master list of changelogs first.
      const changelogsRes = await fetch(
        `/api/inbox/changelogs?locale=${locale}`,
      )
      if (!changelogsRes.ok) throw new Error('Failed to fetch changelogs')
      const fetchedChangelogs = (await changelogsRes.json()) as Changelog[]

      // Step 2: Fetch the server's read status and get the local status.
      const readStatusRes = await fetch('/api/inbox/read-status')
      if (!readStatusRes.ok) throw new Error('Failed to fetch read status')
      const { readChangelogIds: serverReadIds } =
        (await readStatusRes.json()) as {
          readChangelogIds: string[]
        }
      const localReadIds = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || '[]',
      ) as string[]

      let finalReadSet: Set<string>

      // Step 3: Determine the source of truth.
      if (serverReadIds.length > 0) {
        // User is logged in and has read items. Server is the primary source of truth.
        // Merge local and server, giving precedence to server but including local-only items.
        finalReadSet = new Set([...serverReadIds, ...localReadIds])
        // Sync the merged state back to local storage.
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(Array.from(finalReadSet)),
        )
      } else {
        // User is either logged out or has no read items on the server.
        // In this case, localStorage is the only source of truth.
        finalReadSet = new Set(localReadIds)
      }

      // Step 4: Merge changelogs with the correct read status.
      const mergedChangelogs = fetchedChangelogs.map((cl) => ({
        ...cl,
        isRead: finalReadSet.has(cl.id),
      }))

      set({
        changelogs: mergedChangelogs,
        hasUnread: mergedChangelogs.some((cl) => !cl.isRead),
        loading: false,
        error: null,
        lastFetchedContext: { userId: incomingUserId, locale: String(locale) },
        lastFetchedTimestamp: Date.now(),
      })
    } catch (error) {
      console.error('Error fetching changelog inbox data:', error)
      set({
        changelogs: [],
        hasUnread: false,
        loading: false,
        error: 'Failed to load latest updates.',
      })
    }
  },

  markAsRead: (changelogId: string) => {
    // Optimistic UI update
    const currentChangelogs = get().changelogs
    const updatedChangelogs = currentChangelogs.map((cl) =>
      cl.id === changelogId ? { ...cl, isRead: true } : cl,
    )

    set({
      changelogs: updatedChangelogs,
      hasUnread: updatedChangelogs.some((cl) => !cl.isRead),
    })

    // Update localStorage
    const storedReadIds = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || '[]',
    ) as string[]
    const newReadIds = Array.from(new Set([...storedReadIds, changelogId]))
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newReadIds))

    // Send request to the backend
    fetch('/api/inbox/mark-as-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ changelogId }),
    }).catch((error) => {
      console.error('Failed to mark changelog as read on server:', error)
      // Optional: Revert optimistic update on failure
    })
  },
}))
